import { Injectable } from '@angular/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly STORAGE_DIR = 'app_storage';

  private readonly logger = new LoggerService(StorageService.name);

  constructor() {
    this.ensureStorageDirectory();
  }

  private async ensureStorageDirectory(): Promise<void> {
    try {
      await Filesystem.mkdir({
        path: this.STORAGE_DIR,
        directory: Directory.Data,
        recursive: true,
      });

      this.logger.info({
        message: 'Storage directory created successfully',
        identifier: 'movacarpro_storage_directory_created_successfully',
        method: 'ensureStorageDirectory.success',
      });
    } catch (error) {
      this.logger.error({
        message: `Error creating storage directory: ${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_storage_directory_not_created',
        method: 'ensureStorageDirectory.error',
      });
    }
  }

  private getStoragePath(key: string): string {
    return `${this.STORAGE_DIR}/${key}.json`;
  }

  private async readStorageFile(key: string): Promise<any> {
    try {
      const result = await Filesystem.readFile({
        path: this.getStoragePath(key),
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      this.logger.info({
        message: 'Storage file read successfully',
        identifier: 'movacarpro_storage_file_read_successfully',
        method: 'readStorageFile.success',
      });

      try {
        const parsedData = JSON.parse(result.data as string);

        this.logger.info({
          message: 'JSON parsing successful',
          identifier: 'movacarpro_storage_service_json_parse_success',
          method: 'readStorageFile.JSON.parse.success',
        });

        return parsedData;
      } catch (error) {
        this.logger.warn({
          message: `JSON parsing failed: ${error instanceof Error ? error.message : String(error)}`,
          identifier: 'movacarpro_storage_service_json_parse_error',
          method: 'readStorageFile.JSON.parse.error',
        });

        return null;
      }
    } catch (error) {
      this.logger.error({
        message: `Storage file not read: ${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_storage_file_not_read',
        method: 'readStorageFile.error',
      });

      return null;
    }
  }

  private async writeStorageFile(key: string, data: any): Promise<void> {
    await Filesystem.writeFile({
      path: this.getStoragePath(key),
      data: JSON.stringify(data),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  }

  async set(key: string, value: any): Promise<any> {
    await this.writeStorageFile(key, value);

    return value;
  }

  async get(key: string): Promise<any> {
    const value = await this.readStorageFile(key);

    this.logger.info({
      message: value ? 'file retrieved successfully' : 'No file found',
      identifier: value
        ? 'movacarpro_storage_file_found'
        : 'movacarpro_storage_no_file',
      method: 'get',
    });

    return value;
  }

  async delete(key: string): Promise<any> {
    try {
      const value = await this.readStorageFile(key);
      await Filesystem.deleteFile({
        path: this.getStoragePath(key),
        directory: Directory.Data,
      });

      return value;
    } catch (error) {
      return null;
    }
  }

  async clear(): Promise<void> {
    try {
      await Filesystem.rmdir({
        path: this.STORAGE_DIR,
        directory: Directory.Data,
        recursive: true,
      });

      await this.ensureStorageDirectory();
    } catch (error) {
      this.logger.error({
        message: `Failed to clear storage: ${error instanceof Error ? error.message : String(error)}`,
        identifier: 'movacarpro_storage_clear_failed',
        method: 'clear.error',
      });
    }
  }
}
